<template>
  <div class="generate-note">
    <h1>PPT讲稿生成</h1>

    <!-- 上传区域 -->
    <div class="upload-section">
      <el-upload
        class="ppt-upload"
        drag
        action="#"
        :auto-upload="false"
        :on-change="handleFileChange"
        :on-remove="handleFileRemove"
        :file-list="fileList"
        :limit="1"
        accept=".ppt,.pptx"
      >
        <i class="el-icon-upload"></i>
        <div class="el-upload__text">将PPT文件拖到此处，或<em>点击上传</em></div>
        <div class="el-upload__tip" slot="tip">只能上传PPT/PPTX文件</div>
      </el-upload>

      <el-button type="primary" @click="uploadPPT" :loading="uploading" :disabled="!fileList.length">
        <i class="el-icon-document"></i> 上传并生成讲稿
      </el-button>
    </div>

    <!-- 预览区域 -->
    <div v-if="pptLoaded" class="preview-section">
      <div class="preview-container">
        <div class="ppt-preview">
          <h3>PPT预览</h3>
          <div class="ppt-slides">
            <div class="slide-container">
              <img :src="currentSlideUrl" alt="PPT幻灯片" class="slide-image" />
              <div class="slide-controls">
                <el-button size="mini" icon="el-icon-arrow-left" @click="prevSlide" :disabled="currentSlideIndex <= 0"></el-button>
                <span>{{ currentSlideIndex + 1 }} / {{ totalSlides }}</span>
                <el-button size="mini" icon="el-icon-arrow-right" @click="nextSlide" :disabled="currentSlideIndex >= totalSlides - 1"></el-button>
              </div>
            </div>
          </div>
        </div>

        <div class="script-preview">
          <h3>生成的讲稿</h3>
          <div class="script-content" v-html="formatMessage(generatedScript)"></div>
          <div class="script-actions">
            <el-button type="success" @click="downloadScript" :disabled="!generatedScript">
              <i class="el-icon-download"></i> 下载讲稿
            </el-button>
            <el-button type="primary" @click="regenerateScript" :disabled="!pptLoaded || generating">
              <i class="el-icon-refresh"></i> 重新生成
            </el-button>
          </div>
        </div>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="generating && !generatedScript" class="loading-container">
      <div class="loading-animation">
        <i class="el-icon-loading"></i>
      </div>
      <p>正在分析PPT并生成讲稿，请稍候...</p>
    </div>
  </div>
</template>

<script>
import { textCompletion } from '@/api/ai'
import { marked } from 'marked'
import hljs from 'highlight.js'
import 'highlight.js/styles/atom-one-dark.css'

export default {
  name: 'GenerateNote',
  data() {
    return {
      fileList: [],
      uploading: false,
      generating: false,
      pptLoaded: false,
      generatedScript: '',
      currentSlideIndex: 0,
      totalSlides: 10, // 模拟幻灯片总数
      slides: [], // 存储幻灯片图片URL
      pptContent: {} // 存储PPT内容信息
    }
  },
  computed: {
    currentSlideUrl() {
      // 模拟幻灯片URL，实际项目中应该从后端获取
      if (this.slides.length > 0 && this.currentSlideIndex < this.slides.length) {
        return this.slides[this.currentSlideIndex]
      }
      // 返回默认图片
      return 'https://via.placeholder.com/800x450?text=PPT+Slide+' + (this.currentSlideIndex + 1)
    }
  },
  methods: {
    // 文件选择变更
    handleFileChange(file, fileList) {
      this.fileList = fileList.slice(-1) // 只保留最新的一个文件
    },

    // 移除文件
    handleFileRemove(file, fileList) {
      this.fileList = fileList
      this.pptLoaded = false
      this.generatedScript = ''
    },

    // 上传PPT并处理
    uploadPPT() {
      if (!this.fileList.length) {
        this.$message.warning('请先选择PPT文件')
        return
      }

      const file = this.fileList[0].raw
      if (!file.name.toLowerCase().endsWith('.ppt') && !file.name.toLowerCase().endsWith('.pptx')) {
        this.$message.error('请上传PPT格式的文件')
        return
      }

      this.uploading = true
      this.generating = true

      // 模拟上传过程
      setTimeout(() => {
        this.uploading = false
        this.pptLoaded = true

        // 模拟幻灯片数据
        this.totalSlides = Math.floor(Math.random() * 5) + 5 // 随机5-10张幻灯片
        this.slides = []
        for (let i = 0; i < this.totalSlides; i++) {
          this.slides.push(`https://via.placeholder.com/800x450?text=PPT+Slide+${i + 1}`)
        }

        // 提取PPT内容（实际项目中应该从后端获取）
        this.pptContent = {
          title: file.name.replace(/\.[^/.]+$/, ""),
          slides: this.totalSlides,
          topics: ['主题1', '主题2', '主题3']
        }

        // 生成讲稿
        this.generateScript()
      }, 1500)
    },

    // 使用AI生成讲稿
    generateScript() {
      this.generating = true

      // 构建提示词
      const prompt = `请为以下PPT生成一份详细的讲稿：
      
标题：${this.pptContent.title}
幻灯片数量：${this.pptContent.slides}
主要内容：${this.pptContent.topics.join('、')}

请生成一份完整的讲稿，包括开场白、正文讲解和结束语。讲稿应该条理清晰，语言生动，适合演讲使用。`

      // 调用AI接口
      textCompletion(prompt)
        .then(this.handleApiResponse)
        .catch(this.handleApiError)
        .finally(() => {
          this.generating = false
        })
    },

    // 处理API响应
    handleApiResponse(response) {
      let aiResponse = ''

      // 处理不同的响应格式
      if (response.code === 1) {
        aiResponse = response.data
      } else if (response.code === 200) {
        aiResponse = response.data || response.result || response.message || ''
      } else if (typeof response === 'string') {
        aiResponse = response
      } else {
        aiResponse = '抱歉，无法生成讲稿。'
        this.$message.error(response.msg || '请求失败')
      }

      this.generatedScript = aiResponse
    },

    // 处理API错误
    handleApiError(error) {
      console.error('生成讲稿失败:', error)

      // 提取更友好的错误信息
      let errorMsg = '抱歉，生成讲稿时出现了问题'

      if (error.response && error.response.data) {
        errorMsg += ': ' + (error.response.data.msg || error.message || '未知错误')
      } else {
        errorMsg += ': ' + (error.message || '未知错误')
      }

      this.generatedScript = errorMsg
      this.$message.error(errorMsg)
    },

    // 格式化消息（使用与AiChatFloat相同的格式化逻辑）
    formatMessage(content) {
      if (!content) return ''

      try {
        // 配置marked选项和自定义渲染器
        const renderer = new marked.Renderer()

        // 自定义代码渲染
        renderer.code = function(code, language) {
          // 处理数学公式
          if (language === 'latex' || language === 'math' || language === 'tex') {
            return `<div class="math-formula">${code}</div>`
          }

          // 处理代码块
          let highlightedCode = code
          let languageClass = ''

          // 尝试进行代码高亮
          if (language && hljs.getLanguage(language)) {
            try {
              highlightedCode = hljs.highlight(language, code).value
              languageClass = `language-${language}`
            } catch (e) {
              console.error('代码高亮失败:', e)
            }
          }

          // 构建代码块HTML
          return `
            <div class="code-block-wrapper">
              <div class="code-block-header">
                <span class="code-language">${language || 'plaintext'}</span>
                <button class="copy-button" onclick="copyCodeToClipboard(this)">
                  <i class="el-icon-document-copy"></i> 复制
                </button>
              </div>
              <pre class="code-block ${languageClass}"><code>${highlightedCode}</code></pre>
            </div>
          `
        }

        // 配置marked选项
        marked.setOptions({
          renderer: renderer,
          breaks: true,
          gfm: true
        })

        // 预处理Markdown内容，特别处理数学公式部分
        let processedContent = content

        // 1. 检测并标准化数学公式标记
        processedContent = processedContent
          .replace(/(?<!\$|\\\()(?<!\\\))\$(?!\$)([^$\n]+?)(?<!\$)\$(?!\$)(?!\\\))/g, '\\($1\\)')
          .replace(/\$\$([^$]+?)\$\$/g, '\\[$1\\]')

        // 2. 处理LaTeX公式的HTML包装
        processedContent = processedContent
          // 行内公式
          .replace(/\\\(/g, '<span class="inline-formula">\\(')
          .replace(/\\\)/g, '\\)</span>')
          // 行间公式
          .replace(/\\\[/g, '<div class="block-formula">\\[')
          .replace(/\\\]/g, '\\]</div>')

        // 应用Markdown转换
        const htmlContent = marked(processedContent)

        return htmlContent
      } catch (e) {
        console.error('Markdown转换失败:', e)
        return content
      }
    },

    // 下载讲稿
    downloadScript() {
      if (!this.generatedScript) {
        this.$message.warning('没有可下载的讲稿')
        return
      }

      // 创建一个Blob对象
      const blob = new Blob([this.generatedScript], { type: 'text/plain;charset=utf-8' })

      // 创建下载链接
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `${this.pptContent.title || 'PPT'}_讲稿.txt`

      // 触发点击事件下载
      document.body.appendChild(link)
      link.click()

      // 清理
      document.body.removeChild(link)
      URL.revokeObjectURL(link.href)

      this.$message.success('讲稿下载成功')
    },

    // 重新生成讲稿
    regenerateScript() {
      if (!this.pptLoaded) {
        this.$message.warning('请先上传PPT')
        return
      }

      this.generateScript()
    },

    // 上一张幻灯片
    prevSlide() {
      if (this.currentSlideIndex > 0) {
        this.currentSlideIndex--
      }
    },

    // 下一张幻灯片
    nextSlide() {
      if (this.currentSlideIndex < this.totalSlides - 1) {
        this.currentSlideIndex++
      }
    }
  }
}
</script>

<style scoped>
.generate-note {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.upload-section {
  margin: 20px 0;
  text-align: center;
}

.ppt-upload {
  margin-bottom: 20px;
}

.preview-section {
  margin-top: 30px;
}

.preview-container {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
}

.ppt-preview, .script-preview {
  flex: 1;
  min-width: 300px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 15px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.ppt-slides {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 15px;
}

.slide-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.slide-image {
  max-width: 100%;
  max-height: 400px;
  border: 1px solid #dcdfe6;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.12);
}

.slide-controls {
  display: flex;
  align-items: center;
  margin-top: 10px;
  gap: 10px;
}

.script-content {
  height: 400px;
  overflow-y: auto;
  padding: 10px;
  background-color: #f9f9f9;
  border-radius: 4px;
  line-height: 1.6;
  text-align: left;
}

.script-actions {
  margin-top: 15px;
  display: flex;
  justify-content: center;
  gap: 10px;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: 50px;
}

.loading-animation {
  font-size: 40px;
  color: #409EFF;
  margin-bottom: 20px;
}

/* 代码块样式 */
.code-block-wrapper {
  margin: 15px 0;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.code-block-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #2d2d2d;
  color: #e6e6e6;
  padding: 8px 16px;
  font-size: 14px;
}

.code-language {
  font-family: monospace;
  font-weight: bold;
}

.copy-button {
  background: transparent;
  border: none;
  color: #e6e6e6;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  transition: all 0.3s;
}

.copy-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.code-block {
  margin: 0;
  padding: 16px;
  background-color: #282c34;
  color: #abb2bf;
  overflow-x: auto;
  font-family: 'Fira Code', Consolas, Monaco, 'Andale Mono', 'Ubuntu Mono', monospace;
  font-size: 14px;
  line-height: 1.5;
  tab-size: 4;
}

/* 数学公式样式 */
.inline-formula {
  display: inline-block;
  margin: 0 2px;
  vertical-align: middle;
}

.block-formula {
  display: block;
  margin: 15px 0;
  text-align: center;
  overflow-x: auto;
  padding: 10px 0;
}

.math-formula {
  display: block;
  margin: 15px 0;
  text-align: center;
  overflow-x: auto;
  padding: 15px 0;
  background-color: #f9f9f9;
  border-radius: 4px;
}
</style>
